import { useState } from "react";
import { Button, Checkbox, Collapsible, Divider, Icon, RadioButton, Text, Tooltip } from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import SwatchesSelector from "../SwatchesModal/SwatchesSelector";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";
import styles from "./RHFSwatchesSelectorWrapper.module.css";
import { AdjustIcon, InfoIcon, VariantIcon } from "@shopify/polaris-icons";
import { AvailableVolumeTemplates } from "../../apps/Bundles/constants";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import MantleDoc, { mantleDocHandles } from "../MantleDoc";
import { useVariantFieldDisabled } from "../FormFields";
import { ProductSourceType } from "../../apps/Bundles/Types";

interface SwatchesSelectorWrapperProps {
    bundleType?: string;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    setOpenDefaultVariantSelector?: (val: boolean) => void;
    productSource: ProductSourceType;
}

const RHFSwatchesSelectorWrapper = ({
    bundleType = "volume",
    SwatchesBundleConfigTouchedRef,
    setOpenDefaultVariantSelector,
    productSource,
}: SwatchesSelectorWrapperProps) => {
    const [expandVariantSelector, setExpandVariantSelector] = useState(false);
    const [open, setOpen] = useState(false);
    const { trackFormSections } = useBundleMixpanelHook();
    const { control, setValue, getValues } = useFormContext();
    const isNonPrimaryVariant = useVariantFieldDisabled();

    const showSwatches = useWatch({ control, name: "show_swatches" });
    const showVariantSelector = useWatch({ control, name: "show_variant_selector" });
    const selectedTemplateVolume = useWatch({ control, name: "selected_template_volume" });
    const variantLabelPosition = useWatch({ control, name: "variant_label_position" });

    const handleSwatchApply = (updated: any) => {
        (["swatchShape", "swatchSize", "swatchOptions", "swatchesProductsMap"] as const).forEach((key) => {
            const val = updated[key];
            if (val !== undefined) {
                setValue(key, val, { shouldDirty: true, shouldValidate: true });
            }
        });
    };

    const bundleValuesForModal = getValues();

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandVariantSelector((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Variant Selector ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <MantleDoc handle={mantleDocHandles[bundleType].setUpVariantSelector} title="Variant selector" />

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandVariantSelector ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>

            <Collapsible
                open={expandVariantSelector}
                id="variant-selector-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <div style={{ paddingBottom: "16px" }}>
                    <Text as="p" variant="bodyMd" tone="subdued">
                        Choose how customers pick product options inside the bundle.
                    </Text>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "16px",
                            gap: "12px",
                        }}
                    >
                        <RadioButton
                            label="Dropdown"
                            helpText="Customers select options from a dropdown list."
                            id="dropdown_selector"
                            checked={!showSwatches}
                            onChange={() => {
                                setValue("show_swatches", false, { shouldDirty: true });
                            }}
                        />
                        <RadioButton
                            label="Swatches"
                            helpText="Show variants as image swatches, color swatches, labels, or dropdown swatches."
                            id="swatches_selector"
                            checked={!!showSwatches}
                            onChange={() => {
                                setValue("show_swatches", true, { shouldDirty: true });
                            }}
                        />
                    </div>

                    {showSwatches && (
                        <div style={{ marginTop: "16px" }}>
                            <Button
                                onClick={() => setOpen((prev) => !prev)}
                                icon={VariantIcon}
                                disabled={isNonPrimaryVariant}
                            >
                                Set up swatches
                            </Button>
                        </div>
                    )}

                    {open && bundleValuesForModal && (
                        <SwatchesSelector
                            productSource={productSource}
                            open={open}
                            setOpen={setOpen}
                            bundleConfig={bundleValuesForModal as any}
                            onApply={handleSwatchApply}
                            SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                        />
                    )}

                    {setOpenDefaultVariantSelector && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                marginTop: "16px",
                                gap: "12px",
                            }}
                        >
                            <Controller
                                name="show_variant_selector"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        label={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                Let Customers Choose Different Variants for Each Item
                                                <div>
                                                    <Tooltip width="wide" content="Only available for vertical layouts">
                                                        <Icon source={InfoIcon} tone="subdued" />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        }
                                        checked={!!field.value}
                                        onChange={field.onChange}
                                        disabled={
                                            selectedTemplateVolume === AvailableVolumeTemplates.HORIZONTAL_BASIC ||
                                            selectedTemplateVolume === AvailableVolumeTemplates.HORIZONTAL_MODERN
                                        }
                                    />
                                )}
                            />

                            <div className={styles.inputWrapper}>
                                <Button
                                    variant="secondary"
                                    icon={AdjustIcon}
                                    onClick={() => {
                                        setOpenDefaultVariantSelector(true);
                                    }}
                                    disabled={
                                        selectedTemplateVolume === AvailableVolumeTemplates.HORIZONTAL_BASIC ||
                                        selectedTemplateVolume === AvailableVolumeTemplates.HORIZONTAL_MODERN ||
                                        !showVariantSelector
                                    }
                                >
                                    Set default variants
                                </Button>
                            </div>

                            <Controller
                                name="show_option_labels_once"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        label="Show option labels only once"
                                        helpText='Shows "Size" / "Color" above item 1 only, instead of repeating them for every item.'
                                        checked={!!field.value && variantLabelPosition !== "beside"}
                                        onChange={field.onChange}
                                        disabled={!showVariantSelector || variantLabelPosition === "beside"}
                                    />
                                )}
                            />

                            <Controller
                                name="hide_item_numbers"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        label="Hide item numbers"
                                        helpText='Hides the "1.", "2." markers next to each item selector.'
                                        checked={!!field.value}
                                        onChange={field.onChange}
                                        disabled={!showVariantSelector}
                                    />
                                )}
                            />

                            <Controller
                                name="use_compact_spacing"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        label="Use compact spacing"
                                        helpText="Sizes each dropdown to fit its longest option instead of stretching to fill the row."
                                        checked={!!field.value}
                                        onChange={field.onChange}
                                        disabled={!showVariantSelector}
                                    />
                                )}
                            />

                            <Divider />

                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                <Text as="p" variant="bodyMd">
                                    Label position
                                </Text>
                                <RadioButton
                                    label="Above the option"
                                    helpText='"Size" sits above the dropdown.'
                                    id="label_position_above"
                                    checked={(variantLabelPosition ?? "above") === "above"}
                                    onChange={() => setValue("variant_label_position", "above", { shouldDirty: true })}
                                />
                                <RadioButton
                                    label="Beside the option"
                                    helpText='"Size" sits next to the dropdown, on the same line.'
                                    id="label_position_beside"
                                    checked={variantLabelPosition === "beside"}
                                    onChange={() => {
                                        setValue("variant_label_position", "beside", { shouldDirty: true });
                                        setValue("show_option_labels_once", false, { shouldDirty: true });
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Collapsible>
        </>
    );
};

export default RHFSwatchesSelectorWrapper;
